import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface UseAdminRpcOptions {
  autoFetch?: boolean;
  params?: Record<string, unknown>;
}

export function useAdminRpc<T>(rpcName: string, options: UseAdminRpcOptions = {}) {
  const { autoFetch = true, params } = options;
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<string | null>(null);

  const paramsKey = params ? JSON.stringify(params) : '';

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: rpcError } = params
        ? await supabase.rpc(rpcName, params)
        : await supabase.rpc(rpcName);
      if (rpcError) throw rpcError;
      setData(result as T);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch admin data');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpcName, paramsKey]);

  useEffect(() => {
    if (autoFetch) fetch();
  }, [autoFetch, fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// ── Typed hooks for each admin section ──

export interface GlobalOverviewData {
  total_users: number;
  active_users: number;
  total_transactions: number;
  total_accounts: number;
  total_money_tracked: number;
  total_budgets: number;
  total_goals: number;
  users_per_month: { month: string; count: number }[] | null;
  transactions_per_month: { month: string; count: number; total_amount: number }[] | null;
}

export interface UserGrowthData {
  new_today: number;
  new_this_week: number;
  new_this_month: number;
  total_users: number;
  avg_daily_signups: number;
  growth_rate: number;
  country_distribution: { country_code: string; count: number }[] | null;
  top_source: string | null;
  acquisition_sources: { source: string; count: number }[] | null;
  signups_over_time: { day: string; count: number }[] | null;
}

export interface ProductUsageData {
  total_transactions: number;
  avg_transactions_per_user: number;
  total_budgets: number;
  total_goals: number;
  total_accounts: number;
  total_money_tracked: number;
  transactions_per_day: { day: string; count: number }[] | null;
  top_banks: { bank: string; count: number }[] | null;
  transaction_types: { type: string; count: number; total_amount: number }[] | null;
  top_categories: { category: string; count: number }[] | null;
}

export interface EngagementData {
  dau: number;
  wau: number;
  mau: number;
  stickiness: number;
  churn_rate: number;
  retention_d1: number;
  retention_d7: number;
  retention_d30: number;
  active_users_over_time: { day: string; active_users: number }[] | null;
  retention_cohorts:
    | {
        cohort_week: string;
        cohort_size: number;
        week_1: number;
        week_2: number;
        week_3: number;
        week_4: number;
      }[]
    | null;
}

export interface VisitorAnalyticsData {
  visitors_today: number;
  visitors_yesterday: number;
  visitors_this_week: number;
  visitors_this_month: number;
  page_views_today: number;
  page_views_this_week: number;
  visitors_per_day: { day: string; visitors: number; page_views: number }[] | null;
  visitors_per_week: { week: string; visitors: number; page_views: number }[] | null;
  top_pages: { page: string; views: number }[] | null;
}

export const useGlobalOverview = (excludeAdmins = false) =>
  useAdminRpc<GlobalOverviewData>('admin_global_overview', {
    params: { p_exclude_admins: excludeAdmins },
  });
export const useUserGrowth = (excludeAdmins = false) =>
  useAdminRpc<UserGrowthData>('admin_user_growth', {
    params: { p_exclude_admins: excludeAdmins },
  });
export const useProductUsage = (excludeAdmins = false) =>
  useAdminRpc<ProductUsageData>('admin_product_usage', {
    params: { p_exclude_admins: excludeAdmins },
  });
export const useEngagement = (excludeAdmins = false) =>
  useAdminRpc<EngagementData>('admin_engagement', {
    params: { p_exclude_admins: excludeAdmins },
  });
export const useVisitorAnalytics = (excludeAdmins = false) =>
  useAdminRpc<VisitorAnalyticsData>('admin_visitor_analytics', {
    params: { p_exclude_admins: excludeAdmins },
  });
